import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, Box, Typography, List, ListItemButton, ListItemText, TextField, InputAdornment,
  Button, Divider, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { getBlock, blockTypes } from '@atlas/blocks';
import { Preview } from './Preview';
import { sampleProps } from './sample';
import { blockLabel } from './Editor';
import type { Api } from './api';

// "Add a section" picker: a two-pane modal — searchable section list on the
// left, a live preview of the highlighted section on the right. The preview
// uses the section's REAL props from an existing page (real imagery + copy, as
// seen on the live site) when available, falling back to realistic sample data
// for sections not yet used anywhere.
export function SectionPicker({
  open, onClose, onPick, api, tenantId,
}: { open: boolean; onClose: () => void; onPick: (type: string, props: Record<string, unknown>) => void; api: Api; tenantId: string }) {
  const types = useMemo(() => blockTypes().slice().sort((a, b) => blockLabel(a).localeCompare(blockLabel(b))), []);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string>(types[0] ?? '');
  const [real, setReal] = useState<Record<string, Record<string, unknown>> | null>(null);

  // Fetch real per-type samples once, when the picker first opens.
  useEffect(() => {
    if (!open || real) return;
    api.blockSamples(tenantId).then(setReal).catch(() => setReal({}));
  }, [open, real, api, tenantId]);

  const filtered = useMemo(
    () => types.filter((t) => blockLabel(t).toLowerCase().includes(q.trim().toLowerCase())),
    [types, q],
  );

  // Keep the selection valid within the current filter.
  const active = filtered.includes(sel) ? sel : filtered[0] ?? '';
  const usesLive = Boolean(active && real && real[active] && Object.keys(real[active]).length);
  const previewBlocks = useMemo(() => {
    if (!active) return [];
    if (real && real[active] && Object.keys(real[active]).length) return [{ type: active, props: real[active] }];
    const def = getBlock(active);
    return def ? [{ type: active, props: sampleProps(def.schema) }] : [];
  }, [active, real]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: '86vh', maxHeight: 860, borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
        {/* Left: searchable section list */}
        <Box sx={{ width: 300, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17, mb: 1.5 }}>Add a section</Typography>
            <TextField
              size="small" fullWidth placeholder="Search sections…" value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Box>
          <Divider />
          <List dense sx={{ flexGrow: 1, overflowY: 'auto', py: 0.5 }}>
            {filtered.map((t) => (
              <ListItemButton
                key={t} selected={t === active} onClick={() => setSel(t)}
                sx={{ mx: 1, my: 0.25, borderRadius: 1.5, '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } } }}
              >
                <ListItemText primary={blockLabel(t)} primaryTypographyProps={{ fontSize: 14, fontWeight: t === active ? 700 : 500 }} />
              </ListItemButton>
            ))}
            {filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No sections match “{q}”.</Typography>
            ) : null}
          </List>
        </Box>

        {/* Right: live preview + actions */}
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 700 }}>{active ? blockLabel(active) : 'Preview'}</Typography>
            <Chip size="small" color={usesLive ? 'primary' : 'default'} variant={usesLive ? 'filled' : 'outlined'}
              label={usesLive ? 'Live content' : 'Sample data'} />
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={onClose} startIcon={<CloseIcon />} color="inherit">Cancel</Button>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!active} onClick={() => active && onPick(active, (previewBlocks[0]?.props as Record<string, unknown>) ?? {})}>
              Add this section
            </Button>
          </Box>
          <Box sx={{ flexGrow: 1, minHeight: 0, bgcolor: 'background.default' }}>
            {active ? <Preview key={active} blocks={previewBlocks} /> : null}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
